import type { ApiReturnBlobResource } from '@highimpact-frontend/api-types';
import { HIImageCarousel } from '@highimpact/mui-components';
import {
  forwardRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface ApiReturnImageResource extends ApiReturnBlobResource {
  type: 'image';
  id: number;
  name: string;
  value: string;
}

export interface ImageGalleryDisplayerProps {
  images: readonly ApiReturnImageResource[];
}

const MIN_THUMB_PX = 64;
const GALLERY_GAP_PX = 8;
const DEFAULT_COLUMNS = 3;

const ImageGalleryDisplayer = forwardRef<
  HTMLElement,
  ImageGalleryDisplayerProps
>(({ images }, _ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselInitialIndex, setCarouselInitialIndex] = useState(0);

  const carouselImages = useMemo(
    () =>
      images.map((image) => ({
        src: `data:image/png;base64,${image.value}`,
        alt: image.name,
      })),
    [images]
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const measureTarget =
      (container.closest('.widget') as HTMLElement | null) ?? container;

    const measure = () => {
      const containerStyles = getComputedStyle(container);
      const paddingX =
        parseFloat(containerStyles.paddingLeft) +
        parseFloat(containerStyles.paddingRight);
      setContainerWidth(Math.max(0, measureTarget.clientWidth - paddingX));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(measureTarget);
    return () => observer.disconnect();
  }, []);

  if (images.length === 0) {
    return <span>Ressource manquante</span>;
  }

  const availableWidth = Math.max(containerWidth, 0);
  const maxColumns =
    availableWidth > 0
      ? Math.max(
          1,
          Math.floor(
            (availableWidth + GALLERY_GAP_PX) / (MIN_THUMB_PX + GALLERY_GAP_PX)
          )
        )
      : DEFAULT_COLUMNS;
  const columns = Math.max(1, Math.min(maxColumns, images.length));
  const thumbSize =
    availableWidth > 0
      ? Math.floor(
          (availableWidth - (columns - 1) * GALLERY_GAP_PX) / columns
        )
      : MIN_THUMB_PX;

  return (
    <div ref={containerRef} className="image-gallery-displayer">
      <div
        className="image-gallery-grid"
        style={{
          display: 'grid',
          gridAutoFlow: 'row',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gridAutoRows: `${thumbSize}px`,
          width: '100%',
        }}
      >
        {images.map((image, index) => (
          <div
            key={image.id}
            className="image-gallery-grid__item image-gallery-grid__item--clickable"
            role="button"
            tabIndex={0}
            onClick={() => {
              setCarouselInitialIndex(index);
              setCarouselOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setCarouselInitialIndex(index);
                setCarouselOpen(true);
              }
            }}
          >
            <img
              src={`data:image/png;base64,${image.value}`}
              alt={image.name}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      <HIImageCarousel
        images={carouselImages}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        initialIndex={carouselInitialIndex}
      />
    </div>
  );
});

export default ImageGalleryDisplayer;
